import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../../../components/Common";
import { PaginateQuery, TeacherDto } from "../../../../interfaces";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
  TableSpinner,
  TextColumn,
} from "../../../../components";
import {
  ActionTypeEnum,
  RoleEnum,
  UserAttemptType,
  VerificationMethodEnum,
} from "../../../../enums";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import { TeacherClassesModal } from "./TeacherClassesModal";
import { SendPushNotificationModal } from "../../../NotifyUsers";
import { useGetTeacherAdminUsersQuery } from "../../../../store/api/teacherAdminApi";
import {
  useDeleteUserMutation,
  useResetUserAttemptMutation,
  useToggleBlockUserMutation,
  useToggleVerifyUserMutation,
  useUndoDeleteUserMutation,
} from "../../../../store/api/userApi";
import { useGetTeachersQuery } from "../../../../store/api/teacherApi";

type TProps = {};
export const TeacherUsersPage: React.FC<TProps> = () => {
  //#region hooks

  const history = useHistory();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery();

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showBlockUnblockModal, setShowBlockUnblockModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUndoDeleteModal, setShowUndoDeleteModal] = useState(false);
  const [showVerifyUnVerifyModal, setShowVerifyUnVerifyModal] = useState(false);
  const [showResetUserAttemptModal, setShowResetUserAttemptModal] =
    useState(false);
  const [showClassesModal, setShowClassesModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherDto | null>();
  const [filterGenderId, setFilterGenderId] = useState<string>("");
  const [filterPhoneVerificationStatus, setFilterPhoneVerificationStatus] =
    useState<string>("");
  const [selectedRows, setSelectedRows] = useState<TeacherDto[]>([]);
  const [toggledClearRows, setToggleClearRows] = React.useState(false);
  const [showSendPushNotificationModal, setShowSendPushNotificationModal] =
    useState(false);
  const [resetUserAttemptTitle, setResetUserAttemptTitle] =
    useState<string>("");
  const [resetUserAttemptType, setResetUserAttemptType] =
    useState<UserAttemptType>();
  const [resetUserAttemptMessage, setResetUserAttemptMessage] =
    useState<string>("");
  //#endregion

  //#region Api

  const {
    data: teachersResponse,
    isLoading: loading,
    refetch,
  } = useGetTeachersQuery(paginateQuery);

  const [
    toggleBlockUser,
    {
      isLoading: toggleBlockUserLoading,
      isSuccess: toggleBlockUserSuccess,
      error: toggleBlockUserError,
    },
  ] = useToggleBlockUserMutation();

  const [
    toggleVerifyUser,
    {
      isLoading: toggleVerifyUserLoading,
      isSuccess: toggleVerifyUserSuccess,
      error: toggleVerifyUserError,
    },
  ] = useToggleVerifyUserMutation();

  const [
    deleteUser,
    {
      isLoading: deleteUserLoading,
      isSuccess: deleteUserSuccess,
      error: deleteUserError,
    },
  ] = useDeleteUserMutation();

  const [
    undoDeleteUser,
    {
      isLoading: undoDeleteUserLoading,
      isSuccess: undoDeleteUserSuccess,
      error: undoDeleteUserError,
    },
  ] = useUndoDeleteUserMutation();

  const [
    resetUserAttempt,
    {
      isLoading: resetUserAttemptLoading,
      isSuccess: resetUserAttemptSuccess,
      error: resetUserAttemptError,
    },
  ] = useResetUserAttemptMutation();

  //#endregion

  document.title = `${t("documentTitle", { pageName: "teachers" })}`;

  const columns: TableColumn<TeacherDto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <TextColumn text={row.name[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="phoneNumber" />,
      cell: (row) => (
        <a
          style={{ cursor: "pointer", direction: "ltr" }}
          href={`tel:+${row.phoneObj?.countryCode}${row.phoneObj?.phoneNumber}`}
        >
          <u
            style={{ direction: "ltr" }}
          >{`+${row.phoneObj?.countryCode}${row.phoneObj?.phoneNumber}`}</u>
        </a>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="gender" />,
      cell: (row) => <TextColumn text={row.gender ? t(row.gender) : "-"} />,
      sortable: false,
    },
    {
      name: <ColumnHeader title="phoneVerified" />,
      cell: (row) => (
        <>
          <Badge color={row.phoneObj?.isPhoneVerified ? "success" : "danger"}>
            {" "}
            {row.phoneObj?.isPhoneVerified ? t("yes") : t("no")}
          </Badge>
          &nbsp;
          <ActionButton
            color={row.phoneObj?.isPhoneVerified ? "danger" : "success"}
            onAction={() => onTogglePhoneVerify(row)}
            state={row.phoneObj?.isPhoneVerified}
            actionType={ActionTypeEnum.PHONE_VERIFY}
          />
        </>
      ),
      sortable: false,
      width: "260px",
    },
    {
      name: <ColumnHeader title="block" />,
      cell: (row) => (
        <Badge color={!row.isBlocked ? "success" : "danger"}>
          {row.isBlocked ? t("yes") : t("no")}
        </Badge>
      ),
      sortable: false,
      width: "100px",
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      width: "100px",
      cell: (row) => (
        <UncontrolledDropdown>
          <DropdownToggle tag="a" id="dropdownMenuLink2" role="button">
            <i className="ri-more-fill fs-17 dropdown-menu-button-custom"></i>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() =>
                onResetUserAttempt(
                  row,
                  UserAttemptType.INVALID_PASSWORD_ATTEMPT,
                )
              }
            >
              {t("resetInvalidLoginAttempts")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() =>
                onResetUserAttempt(
                  row,
                  UserAttemptType.RESET_PASSWORD_VERIFICATION_ATTEMPT,
                )
              }
            >
              {t("resetForgetPasswordAttempts")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() =>
                onResetUserAttempt(
                  row,
                  UserAttemptType.SIGNUP_VERIFICATION_ATTEMPT,
                )
              }
            >
              {t("resetSignupAttempts")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() => onToggleBlock(row)}
            >
              {row.isBlocked ? t("unblock") : t("block")}
            </DropdownItem>
            <DropdownItem
              style={{
                alignSelf: "flex-start",
                display: "flex",
                alignItems: "center",
              }}
              className="dropdown-item edit-list"
              onClick={() => {
                if (row.isDeleted) onUndoDeleteUser(row);
                else onDelete(row);
              }}
            >
              {row.isDeleted ? t("undoDelete") : t("delete")}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "name.en":
      case "name.ar":
        sortBy = 1;
        break;
      default:
        sortBy = 2;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("created_at:DESC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    if (!toggleBlockUserLoading && toggleBlockUserSuccess) {
      setSelectedTeacher(null);
      setShowBlockUnblockModal(false);
    }
  }, [toggleBlockUserLoading]);

  useEffect(() => {
    if (!toggleVerifyUserLoading && toggleVerifyUserSuccess) {
      setSelectedTeacher(null);
      setShowVerifyUnVerifyModal(false);
    }
  }, [toggleVerifyUserLoading]);

  useEffect(() => {
    if (!deleteUserLoading && deleteUserSuccess) {
      setSelectedTeacher(null);
      setShowDeleteModal(false);
    }
  }, [deleteUserLoading]);

  useEffect(() => {
    if (!undoDeleteUserLoading && undoDeleteUserSuccess) {
      setSelectedTeacher(null);
      setShowUndoDeleteModal(false);
    }
  }, [undoDeleteUserLoading]);

  useEffect(() => {
    if (!resetUserAttemptLoading && resetUserAttemptSuccess) {
      setSelectedTeacher(null);
      setShowResetUserAttemptModal(false);
    }
  }, [resetUserAttemptLoading]);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let filters: any = {};
    if (queryParams.has("filter[gender]")) {
      filters = {
        ...filters,
        ["gender"]: queryParams.get("filter[gender]") || "",
      };
      setFilterGenderId(filters["gender"] as string);
    }
    if (queryParams.has("filter[phoneVerified]")) {
      filters = {
        ...filters,
        ["phoneVerified"]: queryParams.get("filter[phoneVerified]") || "",
      };
      setFilterPhoneVerificationStatus(filters["phoneVerified"] as string);
    }

    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: Object.keys(filters).length > 0 ? filters : paginateQuery.filter,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string),
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy, filter } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      if (
        filter &&
        Object.keys(filter).length &&
        Object.keys(filter).length > 0
      ) {
        let filters: string = "";
        Object.keys(filter).map((key: string) => {
          if (filter[key]) filters += `&filter[${key}]=${filter[key]}`;
        });
        query += filters;
      }
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  //#endregion

  //#region handlers

  const onViewClasses = (_row: TeacherDto) => {
    setSelectedTeacher(_row);
    // setShowClassesModal(true);
  };

  const onToggleBlock = (_row: TeacherDto) => {
    setSelectedTeacher(_row);
    setShowBlockUnblockModal(true);
  };

  const onDelete = (_row: TeacherDto) => {
    setSelectedTeacher(_row);
    setShowDeleteModal(true);
  };

  const onUndoDeleteUser = (_row: TeacherDto) => {
    setSelectedTeacher(_row);
    setShowUndoDeleteModal(true);
  };

  const onTogglePhoneVerify = (_row: TeacherDto) => {
    setSelectedTeacher(_row);
    setShowVerifyUnVerifyModal(true);
  };

  const onBlockUnblockConfirm = () => {
    toggleBlockUser({
      id: selectedTeacher?.id as string,
      state: !(selectedTeacher?.isBlocked as boolean),
    });
    setSelectedTeacher(null);
  };

  const onVerifyConfirm = () => {
    toggleVerifyUser({
      userId: [selectedTeacher?.id as string],
      status: !selectedTeacher?.phoneObj?.isPhoneVerified,
      method: VerificationMethodEnum.PHONE,
    });
    setSelectedTeacher(null);
  };

  const onResetUserAttempt = (_row: TeacherDto, type: UserAttemptType) => {
    setSelectedTeacher(_row);
    setResetUserAttemptType(type);
    if (type === UserAttemptType.INVALID_PASSWORD_ATTEMPT) {
      setResetUserAttemptTitle(t("resetInvalidLoginAttempts"));
      setResetUserAttemptMessage(
        t("resetInvalidLoginAttemptsMessage", {
          username:
            i18n.language === "ar"
              ? selectedTeacher?.name.ar
              : selectedTeacher?.name.en,
        }),
      );
    }
    if (type == UserAttemptType.RESET_PASSWORD_VERIFICATION_ATTEMPT) {
      setResetUserAttemptTitle(t("resetForgetPasswordAttempts"));
      setResetUserAttemptMessage(
        t("resetForgetPasswordAttemptsMessage", {
          username:
            i18n.language === "ar"
              ? selectedTeacher?.name.ar
              : selectedTeacher?.name.en,
        }),
      );
    }
    if (type == UserAttemptType.SIGNUP_VERIFICATION_ATTEMPT) {
      setResetUserAttemptTitle(t("resetSignupAttempts"));
      setResetUserAttemptMessage(
        t("resetSignupAttemptsMessage", {
          username:
            i18n.language === "ar"
              ? selectedTeacher?.name.ar
              : selectedTeacher?.name.en,
        }),
      );
    }
    setShowResetUserAttemptModal(true);
  };

  const onResetUserAttemptConfirm = () => {
    if (resetUserAttemptType) {
      resetUserAttempt({
        id: selectedTeacher?.id as string,
        type: resetUserAttemptType,
      });
    }
    setSelectedTeacher(null);
  };

  const onDeleteConfirm = () => {
    deleteUser(selectedTeacher?.id as string);
    setSelectedTeacher(null);
  };

  const onUndoDeleteConfirm = () => {
    undoDeleteUser(selectedTeacher?.id as string);
    setSelectedTeacher(null);
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column: TableColumn<TeacherDto>, sortDirection: string) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  const onChangeGenderFilter = (value: string) => {
    setFilterGenderId(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["gender"]: value,
      },
    });
  };

  const onChangeFilterPhoneVerification = (value: string) => {
    setFilterPhoneVerificationStatus(value);
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["phoneVerified"]: value,
      },
    });
  };

  const onClearFilters = () => {
    setFilterPhoneVerificationStatus("");
    setFilterGenderId("");
    setPaginateQuery({
      ...paginateQuery,
      filter: {
        ...paginateQuery.filter,
        ["gender"]: "",
        ["phoneVerified"]: "",
      },
    });
  };

  const handleSelectionRows = ({
    selectedRows,
  }: {
    selectedRows: TeacherDto[];
  }) => {
    setSelectedRows(selectedRows);
  };

  const handleClearRows = () => {
    setToggleClearRows(!toggledClearRows);
    setSelectedRows([]);
  };

  const onSendPushNotification = () => {
    if (selectedRows.length > 0) {
      setShowSendPushNotificationModal(true);
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("teachers")} pageTitle={t("appUsers")} />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterGenderId}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeGenderFilter(event.target.value)
                          }
                        >
                          <option value="">{t("filterByGender")}</option>
                          <option value="Female">{t("female")}</option>
                          <option value="Male">{t("male")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <select
                          value={filterPhoneVerificationStatus}
                          className="form-select form-select-md"
                          onChange={(event: any) =>
                            onChangeFilterPhoneVerification(event.target.value)
                          }
                        >
                          <option value="">
                            {t("filterByPhoneVerification")}
                          </option>
                          <option value="verified">{t("verified")}</option>
                          <option value="unverified">{t("unverified")}</option>
                        </select>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          color="success"
                          onClick={() => onClearFilters()}
                        >
                          {t("clearFilters")}
                        </Button>
                        <Button
                          color="success"
                          onClick={() => {
                            setPaginateQuery({ ...paginateQuery });
                            refetch();
                          }}
                        >
                          {t("refresh")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-2 mb-3">
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button
                          disabled={selectedRows.length === 0}
                          color="success"
                          onClick={onSendPushNotification}
                        >
                          <i className="ri-notification-line me-2 align-bottom"></i>
                          {t("sendNotification")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12}>
                      <AppTable
                        selectableRows
                        onSelectedRowsChange={handleSelectionRows}
                        clearSelectedRows={toggledClearRows}
                        theme={"dark"}
                        columns={columns}
                        data={teachersResponse?.data || []}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={teachersResponse?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                  {/* block unblock modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={
                      (selectedTeacher?.isBlocked as boolean)
                        ? t("unblock")
                        : t("block")
                    }
                    description={
                      (selectedTeacher?.isBlocked as boolean)
                        ? t("unblockConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedTeacher?.name.ar
                                : selectedTeacher?.name.en,
                          })
                        : t("blockConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedTeacher?.name.ar
                                : selectedTeacher?.name.en,
                          })
                    }
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={toggleBlockUserLoading}
                    show={showBlockUnblockModal}
                    onConfirmClick={() => onBlockUnblockConfirm()}
                    onCloseClick={() => {
                      setShowBlockUnblockModal(false);
                    }}
                    error={
                      toggleBlockUserError && "data" in toggleBlockUserError
                        ? (toggleBlockUserError.data as any as string)
                        : null
                    }
                  />
                  {/* verify un-verify modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={
                      (selectedTeacher?.phoneObj.isPhoneVerified as boolean)
                        ? t("markUnverified")
                        : t("markVerified")
                    }
                    description={
                      (selectedTeacher?.phoneObj.isPhoneVerified as boolean)
                        ? t("unVerifyPhoneConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedTeacher?.name.ar
                                : selectedTeacher?.name.en,
                          })
                        : t("verifyPhoneConfirmationMessage", {
                            username:
                              i18n.language === "ar"
                                ? selectedTeacher?.name.ar
                                : selectedTeacher?.name.en,
                          })
                    }
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={toggleVerifyUserLoading}
                    show={showVerifyUnVerifyModal}
                    onConfirmClick={() => onVerifyConfirm()}
                    onCloseClick={() => {
                      setShowVerifyUnVerifyModal(false);
                    }}
                    error={
                      toggleVerifyUserError && "data" in toggleVerifyUserError
                        ? (toggleVerifyUserError.data as any as string)
                        : null
                    }
                  />
                  {/* delete modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("deleting")}
                    confirmationButtonTitle={t("delete")}
                    description={t("deleteConfirmationMessage", {
                      username:
                        i18n.language === "ar"
                          ? selectedTeacher?.name.ar
                          : selectedTeacher?.name.en,
                    })}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={deleteUserLoading}
                    show={showDeleteModal}
                    onConfirmClick={() => onDeleteConfirm()}
                    onCloseClick={() => {
                      setShowDeleteModal(false);
                    }}
                    error={
                      deleteUserError && "data" in deleteUserError
                        ? (deleteUserError.data as any as string)
                        : null
                    }
                  />
                  {/* undo delete modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={t("undoDelete")}
                    description={t("undoDeleteConfirmationMessage", {
                      username:
                        i18n.language === "ar"
                          ? selectedTeacher?.name.ar
                          : selectedTeacher?.name.en,
                    })}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={undoDeleteUserLoading}
                    show={showUndoDeleteModal}
                    onConfirmClick={() => onUndoDeleteConfirm()}
                    onCloseClick={() => {
                      setShowUndoDeleteModal(false);
                    }}
                    error={
                      undoDeleteUserError && "data" in undoDeleteUserError
                        ? (undoDeleteUserError.data as any as string)
                        : null
                    }
                  />
                  {/* reset user attempts modal */}
                  <ConfirmationModal
                    confirmationButtonInProgressTitle={t("saving")}
                    confirmationButtonTitle={resetUserAttemptTitle}
                    description={resetUserAttemptMessage}
                    icon={<></>}
                    title={t("areYouSure")}
                    loading={resetUserAttemptLoading}
                    show={showResetUserAttemptModal}
                    onConfirmClick={() => onResetUserAttemptConfirm()}
                    onCloseClick={() => {
                      setShowResetUserAttemptModal(false);
                    }}
                    error={
                      resetUserAttemptError && "data" in resetUserAttemptError
                        ? (resetUserAttemptError.data as any as string)
                        : null
                    }
                  />
                  {selectedTeacher &&
                    selectedTeacher.classes &&
                    selectedTeacher.classes.length > 0 && (
                      <TeacherClassesModal
                        classes={selectedTeacher?.classes}
                        show={showClassesModal}
                        onCloseClick={() => {
                          setShowClassesModal(false);
                        }}
                      />
                    )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <SendPushNotificationModal
          show={showSendPushNotificationModal}
          onCloseClick={() => {
            setShowSendPushNotificationModal(false);
            handleClearRows();
          }}
          mode="user"
          user={selectedRows.map((user) => ({
            id: user.id,
            name: user.name,
          }))}
        />
      )}
    </React.Fragment>
  );
};
